import React, { ReactElement } from 'react';
import { CellTypes, Alignments } from '../table.types';
import { TableCellProps } from './table-cell.types';
import { StyledTableCell, StyledTableHead, SortIndicator, SkeletonWrapper } from './table-cell.style';
import Icon from '../../Icon/icon';
import Skeleton from '../../Skeleton/skeleton';
import COLORS from '../../../style/universal.color';
import Handler from '../../../commonTypes/handler.types';
import { SortOrderTypes } from '../../DataTable/dataTable.types';

const { mlbWhite, mlbGrey3, mlbGrey4, mlbBlack } = COLORS;

const determineIndicatorFill = (dark: boolean, active: boolean) => {
  return active ? (dark ? mlbWhite : mlbBlack) : dark ? mlbGrey3 : mlbGrey4;
};

const buildSortIndicator = (direction: SortOrderTypes, isSelected: boolean, dark: boolean) => {
  const isDescending: boolean = direction === SortOrderTypes.DESC;

  return (
    <SortIndicator isDescending={isDescending} isSelected={isSelected}>
      <Icon name="caret-up" fill={determineIndicatorFill(dark, !isDescending)} />
      <Icon name="caret-down" fill={determineIndicatorFill(dark, isDescending)} />
    </SortIndicator>
  );
};

const TableCell = (props: TableCellProps) => {
  const {
    alignment = Alignments.CENTER,
    content,
    cellType,
    tableIndex,
    columnIndex,
    rowIndex,
    firstColPinned = false,
    lastColPinned = false,
    placeholder = '',
    colgroupStart,
    colgroupEnd,
    colgroupSpan,
    lastColumn = false,
    firstRow = false,
    lastRow = false,
    firstGroupStart,
    cellHighlighted,
    columnHighlighted,
    highlightHead = true,
    fixedWidth,
    dark,
    handlers = {},
    columnSortIndex,
    sortState,
    sortable,
    highlightSortedColumn,
    highlightColor,
    isLoading,
    replaceCustomCellWithSkeleton = false,
    isCustom,
  } = props;

  let cell: ReactElement;

  // Set aria info
  const ariaColSpan: string = colgroupSpan ? 'colgroup' : 'col';
  const ariaIdHeaderCol: string = `tb-${tableIndex}-header col-${columnIndex}`;
  const ariaIdBodyRow: string = `tb-${tableIndex}-body row-${rowIndex} col-${columnIndex}`;

  // descover if we're in the head or not
  const inHead: boolean = cellType === CellTypes.SUPER_HEADER || cellType === CellTypes.HEADER;
  const normalHeader: boolean = cellType === CellTypes.HEADER;

  const isSelected: boolean = cellType !== CellTypes.SUPER_HEADER && columnSortIndex === columnIndex;
  const showSkeleton = isLoading && (isCustom ? replaceCustomCellWithSkeleton : true);

  // IF we are, or are pinned, build as a th element
  if (inHead || firstColPinned || lastColPinned) {
    const isReactElement: boolean = React.isValidElement(content);

    const cellContent: string | number | ReactElement = showSkeleton ? (
      <SkeletonWrapper>
        <Skeleton width="40px" height="12px" margin="0px" />
      </SkeletonWrapper>
    ) : isReactElement ? (
      content
    ) : (
      <span>{content}</span>
    );
    const sortIndicator: ReactElement | null = !isLoading
      ? buildSortIndicator(sortState as SortOrderTypes, isSelected, dark as boolean)
      : null;

    const headHandlers: Handler = sortable ? handlers : {};

    cell = (
      <React.Fragment>
        <StyledTableHead
          alignment={alignment}
          fixedWidth={fixedWidth}
          firstColPinned={firstColPinned}
          lastColPinned={lastColPinned}
          inHead={inHead}
          dark={dark}
          scope={inHead ? ariaColSpan : 'row'}
          colSpan={colgroupSpan}
          headers={ariaIdHeaderCol}
          columnHighlighted={highlightHead && highlightSortedColumn && (columnHighlighted || isSelected)}
          normalHeader={normalHeader}
          data-col={columnIndex}
          tabIndex={sortable ? 0 : undefined}
          sortable={sortable}
          highlightColor={highlightColor}
          {...headHandlers}
        >
          {sortable && sortIndicator}
          {cellContent}
        </StyledTableHead>
      </React.Fragment>
    );
  } else {
    const tdProps: {
      'data-col': number;
      'data-row': number;
      key: string;
      'data-highlighted'?: boolean;
    } = {
      'data-col': columnIndex,
      'data-row': rowIndex,
      key: `${cellType}-${rowIndex}-${columnIndex}`,
    };

    if (!columnHighlighted && cellHighlighted) {
      tdProps['data-highlighted'] = true;
    }

    const isReactElement: boolean = React.isValidElement(content);
    const useplaceholder: boolean = typeof content === 'undefined';
    const cellText: string | number | ReactElement = useplaceholder ? placeholder : content;
    const cellContent: string | number | ReactElement = showSkeleton ? (
      <SkeletonWrapper>
        <Skeleton width="30px" height="12px" margin="0px" />
      </SkeletonWrapper>
    ) : isReactElement ? (
      content
    ) : (
      <span>{cellText}</span>
    );
    const ariaHeaderSource: string | undefined = firstColPinned || lastColPinned ? ariaIdBodyRow : undefined;

    const notPinnedCol: boolean = true;

    cell = (
      <StyledTableCell
        colgroupStart={colgroupStart}
        colgroupEnd={colgroupEnd}
        alignment={alignment}
        fixedWidth={fixedWidth}
        colIndex={columnIndex}
        lastColumn={lastColumn}
        firstRow={firstRow}
        lastRow={lastRow}
        firstGroupStart={firstGroupStart}
        dark={dark}
        scope="row"
        headers={ariaHeaderSource}
        id={notPinnedCol ? ariaIdBodyRow : undefined}
        columnHighlighted={highlightSortedColumn && (columnHighlighted || isSelected)}
        cellHighlighted={cellHighlighted}
        highlightHead={highlightHead}
        highlightColor={highlightColor}
        {...tdProps}
      >
        {cellContent}
      </StyledTableCell>
    );
  }
  return <React.Fragment>{cell}</React.Fragment>;
};

export default TableCell;
